import { createClient } from "@/app/utils/supabase/client";
import {
  Task,
  TaskInsert,
  TaskUpdate,
  TaskAttachment,
  TaskAttachmentInsert,
  TaskAttachmentUpdate,
  TaskChecklistItem,
  TaskChecklistItemInsert,
  TaskChecklistItemUpdate,
  TaskComment,
  TaskCommentInsert,
  TaskCommentUpdate,
  TaskMention,
  TaskMentionInsert,
} from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "@/app/lib/supabase/supabaseDBConstants";


export const tasksApi = {
  // Task operations
  async getTasks(companyId: string, userId?: string, userRole?: string): Promise<Task[]> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.TASKS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    // If user is a Worker, only show tasks assigned to them or where they're mentioned
    if (userRole === "quality_control" && userId) {
      query = query.or(`assigned_to.eq.${userId},id.in.(
        select task_id from ${SUPABASE_TABLES.TASK_MENTIONS} where mentioned_id='${userId}'
      )`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching tasks:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksByProject(projectId: string, userId?: string, userRole?: string): Promise<Task[]> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.TASKS)
      .select("*")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    // If user is a Worker, only show tasks assigned to them or where they're mentioned
    if (userRole === "quality_control" && userId) {
      query = query.or(`assigned_to.eq.${userId},id.in.(
        select task_id from ${SUPABASE_TABLES.TASK_MENTIONS} where mentioned_id='${userId}'
      )`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching tasks by project:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksByProductionStage(productionStageId: string, userId?: string, userRole?: string): Promise<Task[]> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.TASKS)
      .select("*")
      .eq("production_stage_id", productionStageId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    // If user is a Worker, only show tasks assigned to them or where they're mentioned
    if (userRole === "quality_control" && userId) {
      query = query.or(`assigned_to.eq.${userId},id.in.(
        select task_id from ${SUPABASE_TABLES.TASK_MENTIONS} where mentioned_id='${userId}'
      )`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching tasks by production stage:", error);
      throw error;
    }

    return data || [];
  },

  async getTask(taskId: string): Promise<Task> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASKS)
      .select("*")
      .eq("id", taskId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching task:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Task not found");
    }

    return data;
  },

  async createTask(task: TaskInsert): Promise<Task> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASKS)
      .insert(task)
      .select("*")
      .single();

    if (error) {
      console.error("Error creating task:", error);
      throw error;
    }

    return data;
  },

  async updateTask(taskId: string, updates: TaskUpdate): Promise<Task> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASKS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", taskId)
      .select("*")
      .single();

    if (error) {
      console.error("Error updating task:", error);
      throw error;
    }

    return data;
  },

  async deleteTask(taskId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.TASKS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", taskId);

    if (error) {
      console.error("Error deleting task:", error);
      throw error;
    }
  },

  // Task attachment operations
  async getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_ATTACHMENTS)
      .select("*")
      .eq("task_id", taskId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching task attachments:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksAttachments(taskIds: string[]): Promise<TaskAttachment[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_ATTACHMENTS)
      .select("*")
      .in("task_id", taskIds)

    if (error) {
      console.error("Error fetching task attachments:", error);
      throw error;
    }

    return data || [];
  },

  async createTaskAttachment(attachment: TaskAttachmentInsert): Promise<TaskAttachment> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_ATTACHMENTS)
      .insert(attachment)
      .select()
      .single();

    if (error) {
      console.error("Error creating task attachment:", error);
      throw error;
    }

    return data;
  },

  async updateTaskAttachment(attachmentId: string, updates: TaskAttachmentUpdate): Promise<TaskAttachment> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_ATTACHMENTS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", attachmentId)
      .select()
      .single();

    if (error) {
      console.error("Error updating task attachment:", error);
      throw error;
    }

    return data;
  },

  // TODO: this won't work, as task attachments don't have a deleted_at column
  async deleteTaskAttachment(attachmentId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.TASK_ATTACHMENTS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", attachmentId);

    if (error) {
      console.error("Error deleting task attachment:", error);
      throw error;
    }
  },

  // Task checklist item operations
  async getTaskChecklistItems(taskId: string): Promise<TaskChecklistItem[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_CHECKLIST_ITEMS)
      .select("*")
      .eq("task_id", taskId)
      .order("display_order", { ascending: true });

    if (error) {
      console.error("Error fetching task checklist items:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksChecklistItems(taskIds: string[]): Promise<TaskChecklistItem[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_CHECKLIST_ITEMS)
      .select("*")
      .in("task_id", taskIds)
      .order("display_order", { ascending: true });

    if (error) {
      console.error("Error fetching task checklist items:", error);
      throw error;
    }

    return data || [];
  },

  async createTaskChecklistItem(item: TaskChecklistItemInsert): Promise<TaskChecklistItem> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_CHECKLIST_ITEMS)
      .insert(item)
      .select()
      .single();

    if (error) {
      console.error("Error creating task checklist item:", error);
      throw error;
    }

    return data;
  },

  async updateTaskChecklistItem(itemId: string, updates: TaskChecklistItemUpdate): Promise<TaskChecklistItem> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_CHECKLIST_ITEMS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", itemId)
      .select()
      .single();

    if (error) {
      console.error("Error updating task checklist item:", error);
      throw error;
    }

    return data;
  },

  async deleteTaskChecklistItem(itemId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.TASK_CHECKLIST_ITEMS)
      .delete()
      .eq("id", itemId);

    if (error) {
      console.error("Error deleting task checklist item:", error);
      throw error;
    }
  },

  async reorderTaskChecklistItems(taskId: string, itemOrders: { id: string; display_order: number }[]): Promise<void> {
    const supabase = createClient();

    // Update each item's display order
    const updates = itemOrders.map(({ id, display_order }) =>
      supabase
        .from(SUPABASE_TABLES.TASK_CHECKLIST_ITEMS)
        .update({
          display_order,
          updated_at: new Date().toISOString()
        })
        .eq("id", id)
        .eq("task_id", taskId)
    );

    const results = await Promise.all(updates);

    const errors = results.filter(result => result.error);
    if (errors.length > 0) {
      console.error("Errors reordering task checklist items:", errors);
      throw new Error("Failed to reorder some checklist items");
    }
  },

  // Task comment operations
  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_COMMENTS)
      .select("*")
      .eq("task_id", taskId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching task comments:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksComments(taskIds: string[]): Promise<TaskComment[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_COMMENTS)
      .select("*")
      .in("task_id", taskIds)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching task comments:", error);
      throw error;
    }

    return data || [];
  },

  async createTaskComment(comment: TaskCommentInsert): Promise<TaskComment> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_COMMENTS)
      .insert(comment)
      .select("*")
      .single();

    if (error) {
      console.error("Error creating task comment:", error);
      throw error;
    }

    return data;
  },

  async updateTaskComment(commentId: string, updates: TaskCommentUpdate): Promise<TaskComment> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_COMMENTS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", commentId)
      .select("*")
      .single();

    if (error) {
      console.error("Error updating task comment:", error);
      throw error;
    }

    return data;
  },

  async deleteTaskComment(commentId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.TASK_COMMENTS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", commentId);

    if (error) {
      console.error("Error deleting task comment:", error);
      throw error;
    }
  },

  // Task mention operations
  async getTaskMentions(taskId: string): Promise<TaskMention[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .select("*")
      .eq("task_id", taskId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching task mentions:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksMentions(taskIds: string[]): Promise<TaskMention[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .select("*")
      .in("task_id", taskIds)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching task mentions:", error);
      throw error;
    }

    return data || [];
  },

  async getTasksByMentionedId(mentionedId: string): Promise<Task[]> {
    const supabase = createClient();

    const { data: mentions, error: mentionError } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .select("task_id")
      .eq("mentioned_id", mentionedId);

    if (mentionError) {
      console.error("Error fetching user mentions:", mentionError);
      throw mentionError;
    }

    if (!mentions || mentions.length === 0) {
      return [];
    }

    const taskIds = mentions.map(m => m.task_id);

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASKS)
      .select("*")
      .in("id", taskIds)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching tasks by mentions:", error);
      throw error;
    }

    return data || [];
  },

  async createTaskMention(mention: TaskMentionInsert): Promise<TaskMention> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .insert(mention)
      .select("*")
      .single();

    if (error) {
      console.error("Error creating task mention:", error);
      throw error;
    }

    return data;
  },

  async deleteTaskMention(mentionId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .delete()
      .eq("id", mentionId);

    if (error) {
      console.error("Error deleting task mention:", error);
      throw error;
    }
  },

  // Utility function to create multiple mentions at once
  async createTaskMentions(taskMentions: TaskMentionInsert[]): Promise<TaskMention[]> {
    const supabase = createClient();


    const { data, error } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .insert(taskMentions)
      .select("*")

    if (error) {
      console.error("Error creating task mentions:", error);
      throw error;
    }

    return data || [];
  },

  // Task analytics
  async getTaskAnalytics(companyId: string, userId: string): Promise<Task[]> {
    const supabase = createClient();

    // Fetch tasks where user is creator or assignee
    const { data: tasks, error: tasksError } = await supabase
      .from(SUPABASE_TABLES.TASKS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .or(`created_by.eq.${userId},assigned_to.eq.${userId}`);

    if (tasksError) {
      console.error("Error fetching tasks for analytics:", tasksError);
      throw tasksError;
    }

    // Fetch tasks where user is mentioned
    const { data: mentions, error: mentionsError } = await supabase
      .from(SUPABASE_TABLES.TASK_MENTIONS)
      .select("task_id")
      .eq("mentioned_id", userId)
      .eq("mention_type", "user");

    if (mentionsError) {
      console.error("Error fetching task mentions for analytics:", mentionsError);
      throw mentionsError;
    }

    const mentionedTaskIds = mentions?.map(m => m.task_id) || [];

    // Fetch mentioned tasks if any
    let mentionedTasks: Task[] = [];
    if (mentionedTaskIds.length > 0) {
      const { data, error } = await supabase
        .from(SUPABASE_TABLES.TASKS)
        .select("*")
        .in("id", mentionedTaskIds)
        .eq("company_id", companyId)
        .is("deleted_at", null);

      if (error) {
        console.error("Error fetching mentioned tasks for analytics:", error);
      } else {
        mentionedTasks = data || [];
      }
    }

    // Combine all tasks and remove duplicates
    const allTasks = [...(tasks || [])];
    mentionedTasks.forEach(task => {
      if (!allTasks.find(t => t.id === task.id)) {
        allTasks.push(task);
      }
    });

    return allTasks;
  },
};